/**
 * Slice claim / solve / fetch — Voxel Quest API (env-driven, no Cipher Squad URLs).
 */

import { gameState } from '../state/GameState';
import { TILE_STATUS } from '../constants';
import { puzzleEngine } from '../logic/PuzzleEngine';
import { getApiBase, getProgressApiBase } from '../../config/game_config';
import { MYSTERY_TIER, PUBLIC_WORKSHOP_ID } from '../config/tiers';

function response(success, error = null, data = null) {
    return { success, error, data };
}

async function callApi(endpoint, method, body, overrideHeaders = {}) {
    let authHeader = '';
    const telegram = window.Telegram?.WebApp;

    if (telegram?.initData) {
        authHeader = `tma ${telegram.initData}`;
    } else {
        console.warn('[API] No Telegram context — mock auth (local only)');
        authHeader = 'mock dev_user_local';
    }

    const fetchOptions = {
        method,
        headers: {
            'Content-Type': 'application/json',
            Authorization: authHeader,
            'X-Syndicate-Id': gameState.currentSquadId || PUBLIC_WORKSHOP_ID,
            'X-Mystery-Tier': gameState.mysteryTier || MYSTERY_TIER.WORKSHOP,
            ...overrideHeaders,
        },
    };
    if (method !== 'GET' && method !== 'HEAD' && body) {
        fetchOptions.body = JSON.stringify(body);
    }

    try {
        const res = await fetch(`${getApiBase()}${endpoint}`, fetchOptions);
        if (!res.ok) {
            const text = await res.text();
            console.error(`[API] ${res.status}:`, text);
            return { success: false, error: `API_ERROR_${res.status}: ${text}` };
        }
        const validRes = await res.json();
        return { success: true, data: validRes };
    } catch (e) {
        console.error('API Call Failed', e);
        return { success: false, error: 'NETWORK_ERROR' };
    }
}

export async function claimTile(tileId) {
    const user = gameState.currentUser;
    if (!user) return response(false, 'USER_NOT_INITIALIZED');

    const vault = gameState.vault;
    const held = vault.findActiveLockForUser(user.id);
    if (held) {
        gameState.selectTile(held.id);
        return response(false, 'USER_HAS_ACTIVE_LOCK', { tileId: held.id });
    }

    gameState.startSubmit();
    const tile = vault.getTile(tileId);

    if (!tile) {
        gameState.endSubmit(false);
        return response(false, 'TILE_NOT_FOUND');
    }
    if (tile.status !== TILE_STATUS.OPEN) {
        gameState.endSubmit(false);
        return response(false, 'TILE_NOT_OPEN');
    }

    vault.updateTile(tileId, {
        status: TILE_STATUS.CLAIMED,
        claimedBy: user.id,
        lockExpiry: Date.now() + 300000,
    });

    if (gameState.isBootcampMode) {
        gameState.selectedTileId = tileId;
        gameState.endSubmit(true);
        return response(true);
    }

    const apiRes = await callApi('/claim', 'POST', { tileId, sliceId: tileId });
    if (!apiRes.success) {
        vault.updateTile(tileId, { status: TILE_STATUS.OPEN, claimedBy: null, lockExpiry: null });
        gameState.endSubmit(false);
        return response(false, apiRes.error);
    }

    // Merge claimer-only solutionGrid from server so PuzzleEngine can validate.
    const serverTile = apiRes.data?.data || apiRes.data;
    if (serverTile && typeof serverTile === 'object') {
        vault.updateTile(tileId, {
            status: serverTile.status || TILE_STATUS.CLAIMED,
            claimedBy: serverTile.claimedBy || user.id,
            lockExpiry: serverTile.lockExpiry || Date.now() + 300000,
            data: {
                ...(tile.data || {}),
                ...(serverTile.data || {}),
            },
        });
    }

    // Open nonogram immediately after a successful claim
    gameState.selectedTileId = tileId;
    gameState.endSubmit(true);
    return response(true, null, serverTile || null);
}

export async function releaseTile(tileId) {
    gameState.vault.updateTile(tileId, {
        status: TILE_STATUS.OPEN,
        claimedBy: null,
        lockExpiry: null,
    });
    if (gameState.isBootcampMode) return response(true);
    await callApi('/release', 'POST', { tileId, sliceId: tileId });
    return response(true);
}

/** Extend an active claim lock (Lock Oil). Server returns authoritative lockExpiry. */
export async function extendTileLock(tileId) {
    const user = gameState.currentUser;
    if (!user) return response(false, 'USER_NOT_INITIALIZED');

    const tile = gameState.vault.getTile(tileId);
    if (!tile || tile.status !== TILE_STATUS.CLAIMED || tile.claimedBy !== user.id) {
        return response(false, 'NO_CLAIM');
    }

    if (gameState.isBootcampMode) {
        const base = Math.max(Date.now(), tile.lockExpiry || Date.now());
        const lockExpiry = base + 5 * 60 * 1000;
        gameState.vault.updateTile(tileId, { lockExpiry });
        return response(true, null, { lockExpiry });
    }

    const apiRes = await callApi('/extend', 'POST', { tileId, sliceId: tileId });
    if (!apiRes.success) return response(false, apiRes.error);

    const lockExpiry =
        apiRes.data?.data?.lockExpiry ??
        apiRes.data?.lockExpiry ??
        null;
    if (lockExpiry) {
        gameState.vault.updateTile(tileId, { lockExpiry });
    }
    return response(true, null, { lockExpiry });
}

export async function solveTile(tileId, solutionPayload) {
    const user = gameState.currentUser;
    if (!user) return response(false, 'USER_NOT_INITIALIZED');

    gameState.startSubmit();
    const vault = gameState.vault;
    const tile = vault.getTile(tileId);

    if (!tile) {
        gameState.endSubmit(false);
        return response(false, 'TILE_NOT_FOUND');
    }
    if (tile.status !== TILE_STATUS.CLAIMED || tile.claimedBy !== user.id) {
        gameState.endSubmit(false);
        return response(false, 'INVALID_CLAIM_OR_OWNER');
    }

    const validation = puzzleEngine.validateSolution(tile, solutionPayload);
    if (!validation.valid) {
        gameState.endSubmit(false);
        return response(false, 'INVALID_SOLUTION', { reason: validation.error });
    }

    // Normalize MARKED(2) → EMPTY(0) for the wire format (server + storage).
    const normalized = Array.isArray(solutionPayload)
        ? solutionPayload.map((row) =>
              Array.isArray(row) ? row.map((c) => (c === 1 ? 1 : 0)) : row
          )
        : solutionPayload;

    vault.updateTile(tileId, {
        status: TILE_STATUS.SOLVED,
        claimedBy: null,
        lockExpiry: null,
        completedBy: user.id,
        completedAt: Date.now(),
    });

    if (gameState.isBootcampMode) {
        gameState.endSubmit(true);
        return response(true, null, { reward: 'BOOTCAMP_PROGRESSED' });
    }

    const apiRes = await callApi('/solve', 'POST', {
        tileId,
        sliceId: tileId,
        solution: normalized,
    });
    if (!apiRes.success) {
        vault.updateTile(tileId, {
            status: TILE_STATUS.CLAIMED,
            claimedBy: user.id,
            lockExpiry: Date.now() + 300000,
        });
        gameState.endSubmit(false);
        return response(false, apiRes.error);
    }

    gameState.endSubmit(true);
    const payload = apiRes.data?.data || apiRes.data || {};
    if (payload.progress) {
        gameState.applyProgress(payload.progress, { announceUnlock: true });
    }
    // Pull peer progress after our solve
    fetchVault().catch(() => {});
    return response(true, null, {
        reward: 'SLICE_RECONSTRUCTED',
        progress: payload.progress,
        tier: payload.tier,
    });
}

export async function fetchVault() {
    const res = await callApi(`?t=${Date.now()}`, 'GET');
    if (res.success && res.data) {
        gameState.syncVault(res.data);
    }
    return res;
}

export async function fetchProgress() {
    let authHeader = '';
    const telegram = window.Telegram?.WebApp;
    if (telegram?.initData) authHeader = `tma ${telegram.initData}`;
    else authHeader = 'mock dev_user_local';
    try {
        const res = await fetch(`${getProgressApiBase()}?t=${Date.now()}`, {
            method: 'GET',
            headers: {
                Authorization: authHeader,
                'X-Syndicate-Id': gameState.currentSquadId || PUBLIC_WORKSHOP_ID,
                'X-Mystery-Tier': gameState.mysteryTier || MYSTERY_TIER.WORKSHOP,
            },
        });
        if (!res.ok) return { success: false, error: `API_ERROR_${res.status}` };
        const json = await res.json();
        if (json?.data) gameState.applyProgress(json.data, { announceUnlock: false });
        return { success: true, data: json.data };
    } catch (e) {
        console.error('[Progress]', e);
        return { success: false, error: 'NETWORK_ERROR' };
    }
}

export async function setPreferredTier(tier) {
    let authHeader = '';
    const telegram = window.Telegram?.WebApp;
    if (telegram?.initData) authHeader = `tma ${telegram.initData}`;
    else authHeader = 'mock dev_user_local';
    try {
        const res = await fetch(`${getProgressApiBase()}/preferred`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
            },
            body: JSON.stringify({ preferredTier: tier }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            return { success: false, error: json.error || `API_ERROR_${res.status}`, data: json.data };
        }
        if (json?.data) gameState.applyProgress(json.data, { announceUnlock: false });
        return { success: true, data: json.data };
    } catch (e) {
        return { success: false, error: 'NETWORK_ERROR' };
    }
}

/** Persist tutorial graduation to D1 so phone ↔ desktop share the same flag. */
export async function completeBootcampProgress() {
    let authHeader = '';
    const telegram = window.Telegram?.WebApp;
    if (telegram?.initData) authHeader = `tma ${telegram.initData}`;
    else authHeader = 'mock dev_user_local';
    try {
        const res = await fetch(`${getProgressApiBase()}/bootcamp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: authHeader,
            },
            body: '{}',
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            return { success: false, error: json.error || `API_ERROR_${res.status}` };
        }
        if (json?.data) gameState.applyProgress(json.data, { announceUnlock: false });
        return { success: true, data: json.data };
    } catch (e) {
        console.warn('[Bootcamp] persist failed', e);
        return { success: false, error: 'NETWORK_ERROR' };
    }
}

export async function fetchLeaderboard(isGlobal = false) {
    const overrideHeaders = isGlobal
        ? {
              'X-Syndicate-Id':
                  gameState.mysteryTier === MYSTERY_TIER.WORKSHOP
                      ? PUBLIC_WORKSHOP_ID
                      : 'public-syndicate-0000',
          }
        : {};
    const res = await callApi(`/leaderboard?t=${Date.now()}`, 'GET', null, overrideHeaders);
    if (!res.success) throw new Error(res.error || 'Failed to fetch leaderboard');
    if (res.data?.success && Array.isArray(res.data.data)) return res.data.data;
    if (Array.isArray(res.data)) return res.data;
    return [];
}
